/**
 * The canonical filter profile an Executive Report is built from.
 *
 * WHY A FIXED PROFILE. Every metric in the report is computed from the analyst's
 * CURATED view — that is deliberate, because the curated view is what the analyst is
 * signing off on (decision 2026-08-11). But the case filter bar is fifteen toggles,
 * none of them persisted (plain useState, reset on every case open), so two reports
 * from the same investigation run could legitimately carry different numbers with
 * nothing recorded to explain it. Rather than disclose which toggles happened to be
 * set, the report REQUIRES a fixed state and refuses to generate until it is in force:
 * fixed state -> reproducible numbers -> nothing to disclose.
 *
 * THE INVESTIGATION DEFAULTS DELIBERATELY DIFFER FROM THIS PROFILE, and that is not a
 * bug to fix. `hidePostWindow`, `hideFailedEvents`, `hideUnresolvedMailAccess` and
 * `hideSafeIpSessions` default to SHOWN on purpose: each can hide information too early,
 * and they are meant to be applied later in the process — a failed sign-in or a
 * post-window event is evidence while the window is still being established, noise only
 * after. The defaults serve the INVESTIGATION; this profile serves the REPORT. Do not
 * "fix" the investigation defaults to match. Reaching this state must therefore be ONE
 * action, never a checklist re-performed from memory — see the modal's "Set the report
 * view".
 *
 * THE SERVER ENFORCES THIS TOO (Test-ExecReportFilterProfile in
 * api/Shared/ExecutiveReportHelper.psm1). This copy exists so the button can be gated
 * before a request is made; it is not the authority. Keep the two lists in step — the
 * server rejects a pack whose filter state is not canonical, so a divergence shows up
 * as a refused report rather than a silently different one.
 */

/** Stamped into filter_snapshot. Bump when the required set changes, so a report
 *  generated before that change stays distinguishable without listing toggles. */
export const REPORT_FILTER_PROFILE = 'canonical-v3';

/**
 * All fifteen filter-bar toggles, in bar order, with the state canonical-v1 requires.
 *
 * `dismissedMode` is THREE-state (`hidden` | `shown` | `only`) and requires exactly
 * `'hidden'`: a truthiness test would accept `'only'`, which shows *nothing but*
 * dismissed events — the opposite of the intended view.
 */
export const CANONICAL_FILTERS = [
  { key: 'hideSafeIPs',              label: 'Safe IPs hidden',                        required: true },
  { key: 'hideKnownApps',            label: 'Safe apps hidden',                       required: true },
  { key: 'hideMsSubstrate',          label: 'MS substrate hidden',                    required: true },
  { key: 'hideOutlookNoise',         label: 'Outlook noise hidden',                   required: true },
  { key: 'hidePostWindow',           label: 'Post-window hidden',                     required: true },
  { key: 'hidePreCompromise',        label: 'Pre-compromise hidden',                  required: true },
  { key: 'hideSafeSessions',         label: 'Safe sessions hidden',                   required: true },
  { key: 'hideSafeIpSessions',       label: 'Session-safe extended',                  required: true },
  { key: 'hideFailedEvents',         label: 'Failed hidden',                          required: true },
  { key: 'hideDlpEvents',            label: 'DLP hidden',                             required: true },
  { key: 'hideUnresolvedMailAccess', label: 'Unresolved mail/attachment reads hidden', required: true },
  { key: 'hideLookback',             label: 'Lookback hidden',                        required: true },
  { key: 'hideEmailIPs',             label: 'Email IPs hidden',                       required: true },
  // THREE-state ('hidden' | 'shown' | 'only'), so this requires the exact string. A
  // truthiness test would accept 'only', which shows NOTHING BUT dismissed events —
  // the opposite of the intended view.
  { key: 'dismissedMode',            label: 'Dismissed hidden',                       required: 'hidden' },
  // Added in canonical-v2 (v1.154.0). Microsoft writes a risk-detection record ABOUT a
  // sign-in, so 222 of 227 rows on the reference case duplicate a sign-in at the same
  // second and state no risk level of their own. Required HIDDEN so report event counts
  // are not inflated by that 1:1 duplication. Like the four toggles above it, this
  // deliberately differs from the investigation default (shown), which exists so the
  // analyst reads the rows on first review.
  { key: 'hideRiskDetections',       label: 'Risk detections hidden',                 required: true },
  // Added in canonical-v3 (v1.171.0). The session-scoped `hidePreCompromise` above cannot
  // reach an event with no session id or on a session the attacker never touched, so on a
  // real case ~198 Exchange and SharePoint rows from before the attacker arrived sat inside
  // the reviewed view. Required HIDDEN: a report about what the attacker did must not count
  // activity that predates them. Like the four before it, this deliberately differs from the
  // investigation default (shown) — activity before the window is a LEAD while the window is
  // still being established.
  { key: 'hidePreWindow',            label: 'Before window hidden',                   required: true },
];

/**
 * Which toggles are not in their required state. Empty means the view is canonical.
 *
 * A missing key counts as WRONG rather than being skipped: a pack that simply omitted a
 * toggle would otherwise pass the gate, which is the hole the server check exists to
 * close.
 */
export function filterProfileDiff(state) {
  const s = state || {};
  return CANONICAL_FILTERS
    .filter((f) => {
      const actual = s[f.key];
      if (actual === undefined || actual === null) return true;
      return actual !== f.required;
    })
    .map((f) => ({ key: f.key, label: f.label, required: f.required, actual: s[f.key] }));
}

/**
 * How to describe one mismatched filter to the analyst.
 *
 * 🔴 The labels are STATE-PHRASED ("Risk detections hidden") because they are the filter
 * pill's engaged label. Listing them under "these filters are not set for reporting"
 * therefore reads BOTH ways and the two readings are opposites — an operator read
 * "Risk detections hidden" as *the report wants them shown*, which is the reverse of what
 * the profile requires. So a row must state the target AND the current state, never the
 * target alone.
 */
export function describeFilterDiff(diff) {
  const d = diff || {};
  const actual = d.actual;
  let now;
  if (actual === undefined || actual === null) now = 'not set';
  else if (actual === true) now = 'on';
  else if (actual === false) now = 'off';
  else now = String(actual);
  return { label: d.label || d.key, now };
}

export function isCanonicalFilterState(state) {
  return filterProfileDiff(state).length === 0;
}

/** The state object to apply. Spread over the case's toggles to reach the report view. */
export function canonicalFilterState() {
  const out = {};
  for (const f of CANONICAL_FILTERS) out[f.key] = f.required;
  return out;
}
